/**
 * Mandala geometry: the single source of truth for zone layout and hit-testing.
 * The GLSL shader reproduces the same radii (passed as uniforms) so visuals and touch agree exactly.
 *
 * Coordinates: pixels, origin top-left, y down. Angles: radians, 0 = east, increasing COUNTER-clockwise
 * (i.e. computed as atan2(-(y - cy), x - cx)). Petal 0 (Sa) is centred on east; petals ascend counter-clockwise.
 */
import { FIFTHS_ORDER, type PitchSpec } from '@tonist/shared';

export const TAU = Math.PI * 2;

/** Ring boundaries as fractions of R (the outer radius of the vajra ring R3). */
export const RING_FRACTIONS = { r0: 0.17, r1: 0.47, r2: 0.78, r3: 1.0 } as const;
/** R = min(w,h)/2 * MANDALA_FILL */
export const MANDALA_FILL = 0.92;

export interface MandalaLayout {
  width: number; height: number;
  cx: number; cy: number;
  /** outer radius of R3 in px */
  R: number;
  /** outer radii of rings R0..R3 in px */
  radii: [number, number, number, number];
}

export function layoutFor(width: number, height: number): MandalaLayout {
  const R = (Math.min(width, height) / 2) * MANDALA_FILL;
  return { width, height, cx: width / 2, cy: height / 2, R,
    radii: [R * RING_FRACTIONS.r0, R * RING_FRACTIONS.r1, R * RING_FRACTIONS.r2, R * RING_FRACTIONS.r3] };
}

export type RingIndex = 0 | 1 | 2 | 3 | 4;

export interface ZoneHit {
  ring: RingIndex;
  /** petal index (rings 1-3), 0 for bindu, fire-ring sector 0..11 for ring 4 */
  index: number;
  /** -1..1 across the zone's angular extent (0 at centre). Bindu: 0. */
  angleInZone: number;
  /** 0..1 from the zone's inner to outer radius (fire ring: 0 at R3, 1 at page edge distance). */
  radiusInRing: number;
  angle: number; radius: number;
  /** pitch for rings 0-3; undefined on the fire ring */
  pitch?: PitchSpec;
  /** pitch class 0..11 for fire ring sectors */
  pitchClass?: number;
}

/** number of petals for a scale with N degrees: N + 1 (Sa' closes the ring) */
export const petalCount = (degrees: number): number => degrees + 1;

export function angleOf(layout: MandalaLayout, x: number, y: number): number {
  const a = Math.atan2(-(y - layout.cy), x - layout.cx);
  return a < 0 ? a + TAU : a;
}

/** Sector index and in-sector offset for `count` equal sectors with sector 0 centred at angle 0. */
export function sectorOf(angle: number, count: number): { index: number; t: number } {
  const w = TAU / count;
  const shifted = (angle + w / 2) % TAU;
  const index = Math.floor(shifted / w) % count;
  const t = ((shifted - index * w) / w) * 2 - 1; // -1..1
  return { index, t };
}

export function hitTest(layout: MandalaLayout, degrees: number, x: number, y: number, bendCentsMax = 45): ZoneHit {
  const dx = x - layout.cx, dy = y - layout.cy;
  const radius = Math.hypot(dx, dy);
  const angle = angleOf(layout, x, y);
  const [r0, r1, r2, r3] = layout.radii;
  const petals = petalCount(degrees);
  if (radius < r0) {
    return { ring: 0, index: 0, angleInZone: 0, radiusInRing: radius / r0, angle, radius, pitch: { degree: 0, octave: -1, bendCents: 0 } };
  }
  if (radius < r3) {
    const ring: RingIndex = radius < r1 ? 1 : radius < r2 ? 2 : 3;
    const inner = ring === 1 ? r0 : ring === 2 ? r1 : r2;
    const outer = ring === 1 ? r1 : ring === 2 ? r2 : r3;
    const { index, t } = sectorOf(angle, petals);
    const bend = bendFromAngle(t, bendCentsMax);
    return { ring, index, angleInZone: t, radiusInRing: (radius - inner) / (outer - inner), angle, radius,
      pitch: { degree: index, octave: ring - 1, bendCents: bend } };
  }
  const { index, t } = sectorOf(angle, 12);
  const edge = Math.hypot(layout.width / 2, layout.height / 2);
  return { ring: 4, index, angleInZone: t, radiusInRing: Math.min(1, (radius - r3) / Math.max(1, edge - r3)), angle, radius, pitchClass: FIFTHS_ORDER[index]! };
}

/** ±bendMax at the petal edges with a dead band of ±(0.27) around the centre (≈ ±12 cents at 45). */
export function bendFromAngle(t: number, bendMax: number): number {
  const dead = 0.27;
  const a = Math.abs(t);
  if (a <= dead) return 0;
  const s = (a - dead) / (1 - dead);
  return Math.sign(t) * s * s * bendMax;
}

export function zoneCentre(layout: MandalaLayout, degrees: number, ring: RingIndex, index: number): { x: number; y: number; angle: number; r: number } {
  const [r0, r1, r2, r3] = layout.radii;
  if (ring === 0) return { x: layout.cx, y: layout.cy, angle: 0, r: 0 };
  const count = ring === 4 ? 12 : petalCount(degrees);
  const angle = (index * TAU) / count;
  const r = ring === 1 ? (r0 + r1) / 2 : ring === 2 ? (r1 + r2) / 2 : ring === 3 ? (r2 + r3) / 2 : r3 + Math.min(layout.width, layout.height) * 0.05;
  return { x: layout.cx + r * Math.cos(angle), y: layout.cy - r * Math.sin(angle), angle, r };
}

/** Stable key for a zone, used for highlight state and DOM ids. */
export const zoneKey = (ring: RingIndex, index: number): string => `${ring}:${index}`;

/** Which cardinal gate (0 = E, 1 = N, 2 = W, 3 = S) a palace petal sits on, or -1. */
export function gateOf(degrees: number, index: number): number {
  const petals = petalCount(degrees);
  for (let g = 0; g < 4; g++) {
    const centreIndex = (g * petals) / 4;
    if (Number.isInteger(centreIndex) && centreIndex === index) return g;
  }
  return -1;
}
